import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X, CurrencyGbp, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { InputField, SelectField, TextareaField } from '@/components/ui/form-field';
import { useFormValidation, rules } from '@/hooks/useFormValidation';
import { fetchBarristers, fetchBarristerRateCard, type ApiBarrister } from '@/lib/barristersApi';
import { createFeeRecord, updateFeeRecord, type CreateFeeRecordPayload } from '@/lib/feesApi';
import type { TimeEntry } from '@/lib/timeEntriesStore';
import { useAuthStore } from '@/features/auth/store';
import { getApiErrorMessage } from '@/lib/api';
import { sanitizeMoneyInput, parseMoney, formatMoney2 } from '@/lib/money';
import { ScrollLock } from '@/components/shared/ScrollLock';

type FeeType = CreateFeeRecordPayload['feeType'];

const FEE_TYPES: { value: FeeType; label: string; needsHours: boolean }[] = [
  { value: 'brief_fee', label: 'Brief fee', needsHours: false },
  { value: 'refresher', label: 'Refresher', needsHours: false },
  { value: 'hourly', label: 'Hourly', needsHours: true },
  { value: 'fixed', label: 'Fixed', needsHours: false },
  { value: 'conference', label: 'Conference', needsHours: true },
  { value: 'opinion', label: 'Opinion', needsHours: true },
  { value: 'cfa', label: 'CFA', needsHours: false },
  { value: 'dba', label: 'DBA', needsHours: false },
  { value: 'pro_bono', label: 'Pro bono', needsHours: false },
];

interface Props {
  caseId: string;
  caseLabel: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** When set, the modal edits this record (PATCH /fees/:id) instead of creating one. */
  record?: TimeEntry | null;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const BARRISTER_TYPES = ['silk', 'junior', 'pupil', 'door_tenant'];

export function NewFeeRecordModal({ caseId, caseLabel, open, onClose, onSuccess, record }: Props) {
  const user = useAuthStore((s) => s.user);
  const isEdit = !!record;
  const isInvoiced = record?.status === 'Billed';
  // A barrister recording their OWN fee is the fee-earner by definition — never make
  // them pick themselves. Clerks / chambers staff acting on a barrister's behalf still choose.
  const isOwnBarrister = !!user && BARRISTER_TYPES.includes(user.memberType) && !!user.barristerId;
  const ownBarristerName = user ? `${user.firstName} ${user.lastName}`.trim() : '';

  const [barristers, setBarristers] = useState<ApiBarrister[]>([]);
  const [loadingBarristers, setLoadingBarristers] = useState(false);

  const [barristerId, setBarristerId] = useState('');
  const [feeType, setFeeType] = useState<FeeType>('brief_fee');
  const [description, setDescription] = useState('');
  const [feeDate, setFeeDate] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [hours, setHours] = useState('');
  const [rate, setRate] = useState('');
  const [isBillable, setIsBillable] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const selectedFeeMeta = useMemo(
    () => FEE_TYPES.find((f) => f.value === feeType) ?? FEE_TYPES[0],
    [feeType],
  );

  // Form values for validation
  const formValues = useMemo(
    () => ({ barristerId, feeType, description, feeDate, amount, hours, rate }),
    [barristerId, feeType, description, feeDate, amount, hours, rate]
  );

  // Validation rules
  const { isValid, validateAll, resetValidation, getFieldProps } = useFormValidation(
    formValues,
    {
      validations: {
        barristerId: [rules.required('Barrister')],
        description: [rules.required('Description')],
        feeDate: [rules.required('Date')],
        amount: [
          rules.required('Amount'),
          rules.positiveNumber('Amount'),
        ],
      },
    }
  );

  // Edit mode: prefill from the record. The barrister is fixed at creation (the API does not
  // accept a change), so it is shown read-only and not re-selected.
  useEffect(() => {
    if (!open || !record) return;
    setBarristerId('existing');
    setFeeType(((record.feeType as FeeType) ?? 'brief_fee'));
    setDescription(record.description ?? '');
    setFeeDate(record.feeDateIso ?? todayIso());
    setAmount(record.amount ? record.amount.toFixed(2) : '');
    setHours(record.durationMinutes ? String(record.durationMinutes / 60) : '');
    setRate(record.rate ? String(record.rate) : '');
    setIsBillable(record.isBillable ?? record.status !== 'Non-billable');
  }, [open, record]);

  useEffect(() => {
    if (!open || record) return;
    // A barrister is the fee-earner themselves — default to them, no list needed.
    if (isOwnBarrister) {
      setBarristerId(user!.barristerId!);
      return;
    }
    setLoadingBarristers(true);
    fetchBarristers()
      .then((rows) => setBarristers(rows.filter((b) => b.user.status === 'active')))
      .catch((err) => setApiError(getApiErrorMessage(err, 'Could not load barristers')))
      .finally(() => setLoadingBarristers(false));
  }, [open, isOwnBarrister, user, record]);

  useEffect(() => {
    if (open) return;
    setBarristerId('');
    setFeeType('brief_fee');
    setDescription('');
    setFeeDate(todayIso());
    setAmount('');
    setHours('');
    setRate('');
    setIsBillable(true);
    setApiError(null);
    resetValidation();
  }, [open, resetValidation]);

  useEffect(() => {
    if (!barristerId || barristerId === 'existing' || isEdit) return;
    let mounted = true;
    fetchBarristerRateCard(barristerId).then((r) => {
      if (!mounted) return;
      if (r !== null) {
        // Depending on fee type, we could set different rates, but for now fallback to hourly
        const defaultRate = r.hourlyRate ?? r.briefFeeMin ?? r.refresherRate;
        if (defaultRate) {
          setRate(prev => prev ? prev : String(defaultRate));
        }
      }
    }).catch(console.error);
    return () => { mounted = false; };
  }, [barristerId, isEdit]);

  // For hourly-style fees, the total is always hours × rate — recompute live
  // whenever either changes so the amount stays in sync.
  useEffect(() => {
    if (!selectedFeeMeta?.needsHours) return;
    const h = Number(hours);
    const r = Number(rate);
    if (h > 0 && r > 0) {
      setAmount((h * r).toFixed(2));
    }
  }, [hours, rate, selectedFeeMeta?.needsHours]);

  if (!open) return null;

  const canSubmit = isValid && !submitting;

  const handleSubmit = async () => {
    if (!validateAll()) return;
    if (submitting) return;

    setSubmitting(true);
    setApiError(null);
    try {
      const financialFields = {
        feeType,
        amount: parseMoney(amount),
        isBillable,
        ...(selectedFeeMeta?.needsHours && Number(hours) > 0
          ? { durationMinutes: Math.round(Number(hours) * 60) }
          : {}),
        ...(Number(rate) > 0 ? { rate: Number(rate) } : {}),
      };

      const common = {
        description: description.trim(),
        feeDate,
        ...(isInvoiced ? {} : financialFields),
      };
      if (isEdit && record) {
        await updateFeeRecord(record.id, common);
        toast.success('Fee record updated');
      } else {
        // `isInvoiced` is `record?.status === 'Billed'`, so it can only be true when editing.
        // On the create path it is always false and `financialFields` is always spread — but the
        // conditional spread above widens those keys to optional. Re-spreading here is a runtime
        // no-op that lets the required feeType/amount stay visible to CreateFeeRecordPayload.
        const payload: CreateFeeRecordPayload = { caseId, barristerId, ...common, ...financialFields };
        await createFeeRecord(payload);
        toast.success('Fee record created');
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setApiError(getApiErrorMessage(err, isEdit ? 'Could not update the fee record' : 'Could not create the fee record'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 sm:items-center">
      <ScrollLock />
      <div className="w-full max-w-lg rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <CurrencyGbp className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            <div>
              <div className="text-sm font-semibold text-foreground">{isEdit ? 'Edit fee record' : 'New fee record'}</div>
              <div className="text-xs text-muted-foreground">{caseLabel}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            {isEdit ? (
              <InputField
                label="Barrister"
                value={record?.barrister || '—'}
                readOnly
                disabled
                hint="Set when the fee was recorded"
                onChange={() => {}}
              />
            ) : isOwnBarrister ? (
              <InputField
                label="Barrister"
                value={ownBarristerName}
                readOnly
                disabled
                hint="Your fee"
                onChange={() => {}}
              />
            ) : (
              <SelectField
                label="Barrister"
                value={barristerId}
                onChange={(e) => setBarristerId(e.target.value)}
                disabled={loadingBarristers}
                required
                {...getFieldProps('barristerId')}
              >
                <option value="">{loadingBarristers ? 'Loading…' : 'Select barrister'}</option>
                {barristers.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.fullName}
                  </option>
                ))}
              </SelectField>
            )}
            <SelectField
              label="Fee type"
              value={feeType}
              onChange={(e) => setFeeType(e.target.value as FeeType)}
              required
              disabled={isInvoiced}
            >
              {FEE_TYPES.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </SelectField>
          </div>

          <TextareaField
            label="Description"
            placeholder="What was this fee for?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={3}
            required
            {...getFieldProps('description')}
          />

          <div className="grid grid-cols-3 gap-3">
            <InputField
              type="date"
              label="Date"
              value={feeDate}
              onChange={(e) => setFeeDate(e.target.value)}
              required
              {...getFieldProps('feeDate')}
            />
            <InputField
              type="number"
              label={selectedFeeMeta?.needsHours ? 'Hours' : 'Hours'}
              step="0.25"
              min="0"
              placeholder="0"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              disabled={isInvoiced}
              hint={selectedFeeMeta?.needsHours ? undefined : 'Optional'}
            />
            <InputField
              type="number"
              label="Rate £/h"
              step="1"
              min="0"
              placeholder="0"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              disabled={isInvoiced}
              hint="Optional"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <InputField
              type="text"
              inputMode="decimal"
              label="Amount (£)"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(sanitizeMoneyInput(e.target.value))}
              required
              disabled={isInvoiced}
              hint={selectedFeeMeta?.needsHours && Number(hours) > 0 && Number(rate) > 0
                ? `Auto-calculated: ${hours} h × £${rate} = £${amount}`
                : undefined}
              {...getFieldProps('amount')}
              onBlur={() => { getFieldProps('amount').onBlur?.(); setAmount(formatMoney2(amount)); }}
            />
            <div className="flex items-end pb-1.5">
              <label className={`inline-flex items-center gap-2 text-sm ${isInvoiced ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-600 disabled:opacity-50"
                  checked={isBillable}
                  onChange={(e) => setIsBillable(e.target.checked)}
                  disabled={isInvoiced}
                />
                <span className="text-foreground">Billable</span>
              </label>
            </div>
          </div>

          {apiError && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-300">
              <Warning className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{apiError}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create fee record'}
          </Button>
        </div>
      </div>
    </div>
  );
}
